import React from 'react';
import type {
  MenuAvailabilityItem,
  SnoozeDuration,
} from '../../hooks/useMenuAvailabilityQuery';
import { Button, Dialog, Field, Input, Radio } from '../../ui/v2';
import {
  durationOptions,
  formatDateTime,
  toDateTimeLocal,
} from './menuAvailabilityModel';

const MenuSnoozeDialog: React.FC<{
  item: MenuAvailabilityItem | null;
  nextClosingTime: string | null;
  duration: SnoozeDuration;
  customDateTime: string;
  error: string;
  onDurationChange: (duration: SnoozeDuration) => void;
  onCustomDateTimeChange: (value: string) => void;
  onClose: () => void;
  onStage: () => void;
}> = ({
  item,
  nextClosingTime,
  duration,
  customDateTime,
  error,
  onDurationChange,
  onCustomDateTimeChange,
  onClose,
  onStage,
}) => (
  <Dialog
    open={Boolean(item)}
    onClose={onClose}
    title={`Snooze ${item?.name ?? ''}`}
    description="The item will automatically become available when the snooze ends."
    footer={
      <>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={onStage}>
          Stage snooze
        </Button>
      </>
    }
  >
    <fieldset>
      <legend className="mb-2.5 text-v2-12 font-medium text-v2-fg-2">
        Snooze duration
      </legend>
      <div className="flex flex-col gap-3">
        {durationOptions.map((option) => (
          <Radio
            key={option.value}
            name="snooze-duration"
            value={option.value}
            label={option.label}
            checked={duration === option.value}
            onChange={() => onDurationChange(option.value)}
            description={
              option.value === 'until_close'
                ? nextClosingTime
                  ? `Closes ${formatDateTime(nextClosingTime)}`
                  : 'No future closing time is configured'
                : undefined
            }
          />
        ))}
      </div>
    </fieldset>

    {duration === 'custom' && (
      <Field label="Available again" className="mt-4 max-w-[260px]">
        {(id, describedBy) => (
          <Input
            id={id}
            aria-describedby={describedBy}
            type="datetime-local"
            min={toDateTimeLocal(new Date())}
            value={customDateTime}
            onChange={(event) => onCustomDateTimeChange(event.target.value)}
          />
        )}
      </Field>
    )}

    {error && (
      <p role="alert" className="mt-4 text-v2-12 text-v2-danger">
        {error}
      </p>
    )}
  </Dialog>
);

export default MenuSnoozeDialog;
